import { useSyncExternalStore } from 'react';
import { normalizeProfileId } from '@/data/catalog';
import { t } from '@/i18n';
import { repo } from '@/storage/db';
import type { Asset, FxRates, Message, Portfolio, Quote, Report, ReportTemplateId, Snapshot, Thread } from '@/types';

export interface RunState {
  threadId: string;
  round: number;
  total: number;
  done: number;
  controller: AbortController;
}

export interface AppState {
  ready: boolean;
  portfolios: Portfolio[];
  assets: Asset[];
  quotes: Record<string, Quote | undefined>;
  fx: FxRates | null;
  snapshots: Snapshot[];
  reports: Record<string, Report | undefined>;
  threads: Thread[];
  messages: Record<string, Message[] | undefined>;
  runs: Record<string, RunState | undefined>;
  refreshing: boolean;
  lastRefreshAt: number | null;
  reportBusy: ReportTemplateId | null; // template being generated
  notice: string | null;
  noticeLevel: 'info' | 'error';
  reportErrors: Record<string, string>; // last failure per template, until the next successful run
}

let state: AppState = {
  ready: false, portfolios: [], assets: [], quotes: {}, fx: null, snapshots: [], reports: {}, threads: [], messages: {}, runs: {},
  refreshing: false, lastRefreshAt: null, reportBusy: null, notice: null, noticeLevel: 'info', reportErrors: {},
};
const listeners = new Set<() => void>();
let noticeTimer: ReturnType<typeof setTimeout> | undefined;

function patch(partial: Partial<AppState>) {
  state = { ...state, ...partial };
  for (const l of listeners) l();
}

function omit<T>(record: Record<string, T | undefined>, key: string): Record<string, T | undefined> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}

export const appStore = {
  get: (): AppState => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  async init() {
    const data = await repo.loadAll();
    const quotes: Record<string, Quote> = {};
    for (const q of data.quotes) quotes[q.key] = { ...q, stale: true };
    const reports: Record<string, Report> = {};
    for (const r of data.reports) reports[r.id] = r;
    const threads = data.threads.map((th) => ({ ...th, profileIds: th.profileIds.map(normalizeProfileId) }));
    patch({ ...data, threads, quotes, reports, ready: true });
  },

  // — portfolios & assets —
  async createPortfolio(name: string): Promise<Portfolio> {
    const p: Portfolio = { id: crypto.randomUUID(), name: name.trim() || t('list.newPortfolio'), createdAt: Date.now(), sortOrder: state.portfolios.length };
    await repo.putPortfolio(p);
    patch({ portfolios: [...state.portfolios, p] });
    return p;
  },
  async renamePortfolio(id: string, name: string) {
    const p = state.portfolios.find((x) => x.id === id);
    if (!p) return;
    const next = { ...p, name: name.trim() || p.name };
    await repo.putPortfolio(next);
    patch({ portfolios: state.portfolios.map((x) => (x.id === id ? next : x)) });
  },
  async deletePortfolio(id: string) {
    await repo.deletePortfolio(id);
    patch({ portfolios: state.portfolios.filter((x) => x.id !== id), assets: state.assets.filter((a) => a.portfolioId !== id) });
  },
  async addAsset(input: Omit<Asset, 'id' | 'createdAt'>): Promise<Asset> {
    const a: Asset = { ...input, id: crypto.randomUUID(), createdAt: Date.now() };
    await repo.putAsset(a);
    patch({ assets: [...state.assets, a] });
    return a;
  },
  async updateAsset(id: string, changes: Partial<Asset>) {
    const a = state.assets.find((x) => x.id === id);
    if (!a) return;
    const next = { ...a, ...changes };
    await repo.putAsset(next);
    patch({ assets: state.assets.map((x) => (x.id === id ? next : x)) });
  },
  async deleteAsset(id: string) {
    await repo.deleteAsset(id);
    patch({ assets: state.assets.filter((x) => x.id !== id) });
  },

  // — market data —
  async setQuotes(quotes: Quote[]) {
    if (quotes.length === 0) return;
    const next = { ...state.quotes };
    for (const q of quotes) next[q.key] = q;
    patch({ quotes: next });
    await repo.putQuotes(quotes);
  },
  markStale(keys: string[]) {
    const next = { ...state.quotes };
    for (const k of keys) {
      const q = next[k];
      if (q) next[k] = { ...q, stale: true };
    }
    patch({ quotes: next });
  },
  setFx(fx: FxRates) {
    try {
      localStorage.setItem('assetlist.fx', JSON.stringify(fx));
    } catch {
      // ignore
    }
    patch({ fx });
  },
  loadCachedFx() {
    try {
      const raw = localStorage.getItem('assetlist.fx');
      if (raw) patch({ fx: JSON.parse(raw) as FxRates });
    } catch {
      // ignore
    }
  },
  setRefreshing(refreshing: boolean) {
    patch(refreshing ? { refreshing } : { refreshing, lastRefreshAt: Date.now() });
  },
  async recordSnapshot(s: Snapshot) {
    await repo.putSnapshot(s);
    patch({ snapshots: [...state.snapshots.filter((x) => x.date !== s.date), s].sort((a, b) => a.date.localeCompare(b.date)) });
  },

  // — reports —
  async putReport(r: Report) {
    await repo.putReport(r);
    patch({ reports: { ...state.reports, [r.id]: r } });
  },
  async deleteReport(id: string) {
    await repo.deleteReport(id);
    patch({ reports: omit(state.reports, id) });
  },
  setReportBusy(id: ReportTemplateId | null) {
    patch({ reportBusy: id });
  },

  // — expert threads —
  async createThread(thread: Thread) {
    await repo.putThread(thread);
    patch({ threads: [thread, ...state.threads], messages: { ...state.messages, [thread.id]: [] } });
  },
  async updateThread(id: string, changes: Partial<Thread>) {
    const t = state.threads.find((x) => x.id === id);
    if (!t) return;
    const next = { ...t, ...changes, updatedAt: Date.now() };
    await repo.putThread(next);
    patch({ threads: [next, ...state.threads.filter((x) => x.id !== id)] });
  },
  async deleteThread(id: string) {
    state.runs[id]?.controller.abort();
    await repo.deleteThread(id);
    patch({ threads: state.threads.filter((t) => t.id !== id), messages: omit(state.messages, id), runs: omit(state.runs, id) });
  },
  async ensureMessages(threadId: string) {
    if (state.messages[threadId]) return;
    const list = await repo.listMessages(threadId);
    const repaired = list.map((m) => (m.status === 'streaming' ? { ...m, status: 'error' as const, error: t('notice.interrupted') } : m)).map((m) => (m.profileId ? { ...m, profileId: normalizeProfileId(m.profileId) } : m));
    for (let i = 0; i < repaired.length; i++) if (repaired[i] !== list[i]) await repo.putMessage(repaired[i]);
    patch({ messages: { ...state.messages, [threadId]: repaired } });
  },
  async upsertMessage(message: Message, persist = true) {
    if (persist) await repo.putMessage(message);
    const list = state.messages[message.threadId] ?? [];
    const next = list.some((m) => m.id === message.id) ? list.map((m) => (m.id === message.id ? message : m)) : [...list, message];
    patch({ messages: { ...state.messages, [message.threadId]: next } });
  },
  message(threadId: string, id: string): Message | undefined {
    return state.messages[threadId]?.find((m) => m.id === id);
  },
  setRun(threadId: string, run: RunState | undefined) {
    patch({ runs: run ? { ...state.runs, [threadId]: run } : omit(state.runs, threadId) });
  },
  bumpRunDone(threadId: string) {
    const run = state.runs[threadId];
    if (run) patch({ runs: { ...state.runs, [threadId]: { ...run, done: run.done + 1 } } });
  },

  async clearAll() {
    for (const run of Object.values(state.runs)) run?.controller.abort();
    await repo.clearAll();
    patch({ portfolios: [], assets: [], quotes: {}, snapshots: [], reports: {}, threads: [], messages: {}, runs: {} });
  },
  async replaceAll() {
    const data = await repo.loadAll();
    const reports: Record<string, Report> = {};
    for (const r of data.reports) reports[r.id] = r;
    patch({ portfolios: data.portfolios, assets: data.assets, snapshots: data.snapshots, reports, threads: data.threads, messages: {} });
  },

  /** Info toasts fade after a few seconds; error toasts stay until dismissed, so a 429 detail can actually be read. */
  notify(message: string, level: 'info' | 'error' = 'info') {
    if (noticeTimer) clearTimeout(noticeTimer);
    patch({ notice: message, noticeLevel: level });
    if (level === 'info') noticeTimer = setTimeout(() => patch({ notice: null }), 4500);
  },
  dismissNotice() {
    if (noticeTimer) clearTimeout(noticeTimer);
    patch({ notice: null });
  },
  setReportError(templateId: string, error: string | null) {
    const next = { ...state.reportErrors };
    if (error) next[templateId] = error;
    else delete next[templateId];
    patch({ reportErrors: next });
  },
};

export function useAppState(): AppState {
  return useSyncExternalStore(appStore.subscribe, appStore.get);
}
